import { supabase } from '@/lib/supabase';
import { ChangeRecord } from '@/lib/utils/sync-tracker';

export interface SyncResult {
  success: boolean;
  syncedChanges: string[];
  failedChanges: string[];
  errors: string[];
}

export class IncrementalSyncService {
  // Sync changes for patients table
  static async syncPatientChanges(changes: ChangeRecord[]): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      syncedChanges: [],
      failedChanges: [],
      errors: []
    };

    for (const change of changes) {
      try {
        switch (change.operation) {
          case 'create':
            await this.createPatient(change);
            break;
          case 'update':
            await this.updatePatient(change);
            break;
          case 'delete':
            await this.deletePatient(change);
            break;
        }
        result.syncedChanges.push(`${change.table}:${change.id}:${change.operation}`);
      } catch (error) {
        // Handle duplicate key errors gracefully
        if (error.code === '23505') {
          console.warn(`Record ${change.id} already exists, skipping ${change.operation}`);
          result.syncedChanges.push(`${change.table}:${change.id}:${change.operation}`);
          continue; // Skip to next change
        }
        console.error(`Failed to sync patient change:`, change, error);
        result.failedChanges.push(`${change.table}:${change.id}:${change.operation}`);
        result.errors.push((error as Error).message);
        result.success = false;
      }
    }

    return result;
  }

  // Sync changes for doctors table
  static async syncDoctorChanges(changes: ChangeRecord[]): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      syncedChanges: [],
      failedChanges: [],
      errors: []
    };

    for (const change of changes) {
      try {
        switch (change.operation) {
          case 'create':
            await this.createDoctor(change);
            break;
          case 'update':
            await this.updateDoctor(change);
            break;
          case 'delete':
            await this.deleteDoctor(change);
            break;
        }
        result.syncedChanges.push(`${change.table}:${change.id}:${change.operation}`);
      } catch (error) {
        console.error(`Failed to sync doctor change:`, change, error);
        result.failedChanges.push(`${change.table}:${change.id}:${change.operation}`);
        result.errors.push((error as Error).message);
        result.success = false;
      }
    }

    return result;
  }

  // Sync changes for prescriptions table
  static async syncPrescriptionChanges(changes: ChangeRecord[]): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      syncedChanges: [],
      failedChanges: [],
      errors: []
    };

    for (const change of changes) {
      try {
        switch (change.operation) {
          case 'create':
            await this.createPrescription(change);
            break;
          case 'update':
            await this.updatePrescription(change);
            break;
          case 'delete':
            await this.deletePrescription(change);
            break;
        }
        result.syncedChanges.push(`${change.table}:${change.id}:${change.operation}`);
      } catch (error) {
        console.error(`Failed to sync prescription change:`, change, error);
        result.failedChanges.push(`${change.table}:${change.id}:${change.operation}`);
        result.errors.push((error as Error).message);
        result.success = false;
      }
    }

    return result;
  }

  // Sync changes for consultations table
  static async syncConsultationChanges(changes: ChangeRecord[]): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      syncedChanges: [],
      failedChanges: [],
      errors: []
    };

    for (const change of changes) {
      try {
        switch (change.operation) {
          case 'create':
            await this.createConsultation(change);
            break;
          case 'update':
            await this.updateConsultation(change);
            break;
          case 'delete':
            await this.deleteConsultation(change);
            break;
        }
        result.syncedChanges.push(`${change.table}:${change.id}:${change.operation}`);
      } catch (error) {
        // Handle duplicate key errors gracefully
        if (error.code === '23505') {
          console.warn(`Record ${change.id} already exists, skipping ${change.operation}`);
          result.syncedChanges.push(`${change.table}:${change.id}:${change.operation}`);
          continue; // Skip to next change
        }
        console.error(`Failed to sync consultation change:`, change, error);
        result.failedChanges.push(`${change.table}:${change.id}:${change.operation}`);
        result.errors.push((error as Error).message);
        result.success = false;
      }
    }

    return result;
  }

  // Patient operations
  private static async createPatient(change: ChangeRecord) {
    if (!change.data) throw new Error('No data provided for patient creation');
    
    const { error } = await supabase
      .from('patients')
      .insert([change.data]);

    if (error) {
      // Handle duplicate key error - patient already exists
      if (error.code === '23505') {
        console.warn(`Patient ${change.id} already exists, skipping creation`);
        return; // Skip creation, don't throw error
      }
      throw error;
    }
  }

  private static async updatePatient(change: ChangeRecord) {
    if (!change.data) throw new Error('No data provided for patient update');
    
    const { error } = await supabase
      .from('patients')
      .update(change.data)
      .eq('id', change.id);

    if (error) throw error;
  }

  private static async deletePatient(change: ChangeRecord) {
    const { error } = await supabase
      .from('patients')
      .delete()
      .eq('id', change.id);

    if (error) throw error;
  }

  // Doctor operations
  private static async createDoctor(change: ChangeRecord) {
    if (!change.data) throw new Error('No data provided for doctor creation');
    
    const { error } = await supabase
      .from('doctors')
      .insert([change.data]);

    if (error) throw error;
  }

  private static async updateDoctor(change: ChangeRecord) {
    if (!change.data) throw new Error('No data provided for doctor update');
    
    const { error } = await supabase
      .from('doctors')
      .update(change.data)
      .eq('id', change.id);

    if (error) throw error;
  }

  private static async deleteDoctor(change: ChangeRecord) {
    const { error } = await supabase
      .from('doctors')
      .delete()
      .eq('id', change.id);

    if (error) throw error;
  }

  // Prescription operations
  private static async createPrescription(change: ChangeRecord) {
    if (!change.data) throw new Error('No data provided for prescription creation');
    
    const { error } = await supabase
      .from('prescriptions')
      .insert([change.data]);

    if (error) throw error;
  }

  private static async updatePrescription(change: ChangeRecord) {
    if (!change.data) throw new Error('No data provided for prescription update');
    
    const { error } = await supabase
      .from('prescriptions')
      .update(change.data)
      .eq('id', change.id);

    if (error) throw error;
  }

  private static async deletePrescription(change: ChangeRecord) {
    const { error } = await supabase
      .from('prescriptions')
      .delete()
      .eq('id', change.id);

    if (error) throw error;
  }

  // Consultation operations
  private static async createConsultation(change: ChangeRecord) {
    if (!change.data) throw new Error('No data provided for consultation creation');
    
    const { error } = await supabase
      .from('consultations')
      .insert([change.data]);
    
    if (error) {
      // Handle duplicate key error - consultation already exists
      if (error.code === '23505') {
        console.warn(`Consultation ${change.id} already exists, skipping creation`);
        return; // Skip creation, don't throw error
      }
      throw error;
    }
  }

  private static async updateConsultation(change: ChangeRecord) {
    if (!change.data) throw new Error('No data provided for consultation update');
    const { error } = await supabase
      .from('consultations')
      .update(change.data)
      .eq('id', change.id);
    if (error) throw error;
  }

  private static async deleteConsultation(change: ChangeRecord) {
    const { error } = await supabase
      .from('consultations')
      .delete()
      .eq('id', change.id);
    if (error) throw error;
  }

  // Batch sync multiple tables
  static async batchSync(changesByTable: Map<string, ChangeRecord[]>): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      syncedChanges: [],
      failedChanges: [],
      errors: []
    };

    for (const [table, changes] of changesByTable) {
      try {
        let tableResult: SyncResult;

        switch (table) {
          case 'patients':
            tableResult = await this.syncPatientChanges(changes);
            break;
          case 'doctors':
            tableResult = await this.syncDoctorChanges(changes);
            break;
          case 'prescriptions':
            tableResult = await this.syncPrescriptionChanges(changes);
            break;
          case 'consultations':
            tableResult = await this.syncConsultationChanges(changes);
            break;
          default:
            console.warn(`Unknown table for sync: ${table}`);
            continue;
        }

        // Merge results
        result.syncedChanges.push(...tableResult.syncedChanges);
        result.failedChanges.push(...tableResult.failedChanges);
        result.errors.push(...tableResult.errors);
        
        if (!tableResult.success) {
          result.success = false;
        }
      } catch (error) {
        console.error(`Failed to sync table ${table}:`, error);
        result.success = false;
        result.errors.push(`Table ${table}: ${(error as Error).message}`);
      }
    }

    return result;
  }
} 